import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Loader2, Landmark, Send, AlertTriangle } from 'lucide-react';

export const ACMeetingSettingsDialog = ({ open, onOpenChange, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [meetingDate, setMeetingDate] = useState('');
  const [maxDeals, setMaxDeals] = useState(10);
  const [recipientsRaw, setRecipientsRaw] = useState('');
  const [senderId, setSenderId] = useState('');
  const [lang, setLang] = useState('de');
  const [kpiWeek, setKpiWeek] = useState('0');
  const [connections, setConnections] = useState([]);
  const [lastSentAt, setLastSentAt] = useState(null);
  const [lastError, setLastError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([api.getAcSettings(), api.msConnections()]);
      setEnabled(!!s.data.enabled);
      setMeetingDate(s.data.next_meeting_date || '');
      setMaxDeals(s.data.max_deals || 10);
      setRecipientsRaw((s.data.recipients || []).join(', '));
      setSenderId(s.data.sender_user_id || '');
      setLang(s.data.lang || 'de');
      setKpiWeek(String(s.data.kpi_week_offset ?? 0));
      setLastSentAt(s.data.last_sent_at || null);
      setLastError(s.data.last_error || null);
      setConnections(c.data.connections || []);
    } catch { toast.error('Failed to load AC settings'); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const buildPayload = () => ({
    enabled, next_meeting_date: meetingDate || null, max_deals: Number(maxDeals),
    recipients: recipientsRaw.split(/[\n,;]+/).map(r => r.trim()).filter(r => r.includes('@')),
    sender_user_id: senderId || null, lang,
    kpi_week_offset: Number(kpiWeek),
    app_origin: window.location.origin,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveAcSettings(buildPayload());
      toast.success('AC meeting settings saved');
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleSendNow = async () => {
    if (!window.confirm('Send the AC agenda email now? This is a real send — the included deals will be unflagged.')) return;
    setSending(true);
    try {
      await api.saveAcSettings(buildPayload());
      const res = await api.sendAcEmailNow();
      toast.success(`AC agenda sent to ${res.data.sent_to?.length || 0} recipient(s)`);
      onSaved?.();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    setSending(false);
  };

  const sender = connections.find(c => c.user_id === senderId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="ac-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            <Landmark className="w-5 h-5 text-purple-400" /> AC Meeting — Automated Agenda Email
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 mt-1">
            <p className="text-xs text-muted-foreground">
              Bi-weekly rhythm: the '!'-flagged deals (oldest first, up to the max) are emailed to the AC <strong>3 workdays before</strong> the meeting. Included deals are unflagged automatically; the date then rolls forward by 14 days.
            </p>

            <div className="flex items-center justify-between">
              <p className="text-sm text-foreground font-medium">Enable automatic AC agenda email</p>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="ac-enabled-switch" />
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1">
                <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Next AC meeting</p>
                <Input type="date" value={meetingDate} onChange={e => setMeetingDate(e.target.value)}
                  className="bg-secondary border-border rounded-sm h-9 text-sm text-foreground"
                  data-testid="ac-meeting-date-input" />
              </div>
              <div className="w-28">
                <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Max deals</p>
                <Input type="number" min={1} max={50} value={maxDeals} onChange={e => setMaxDeals(e.target.value)}
                  className="bg-secondary border-border rounded-sm h-9 text-sm text-foreground"
                  data-testid="ac-max-deals-input" />
              </div>
              <div className="w-32">
                <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Email language</p>
                <Select value={lang} onValueChange={setLang}>
                  <SelectTrigger className="bg-secondary border-border rounded-sm h-9 text-sm" data-testid="ac-lang-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border rounded-sm">
                    <SelectItem value="de" className="text-xs">Deutsch</SelectItem>
                    <SelectItem value="en" className="text-xs">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Team-KPI weeks in the email (2-week window)</p>
              <Select value={kpiWeek} onValueChange={setKpiWeek}>
                <SelectTrigger className="bg-secondary border-border rounded-sm h-9 text-sm" data-testid="ac-kpi-week-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border rounded-sm">
                  <SelectItem value="0" className="text-xs">Diese + letzte Woche</SelectItem>
                  <SelectItem value="-1" className="text-xs">Letzte + vorletzte Woche</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">
                AC-Rhythmus ist 2-wöchig — die Mail zeigt je KPI einen Balken pro Woche. „Letzte + vorletzte Woche" wählen, wenn die Mail am Wochenanfang rausgeht.
              </p>
            </div>

            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">AC recipients (comma separated)</p>
              <textarea value={recipientsRaw} onChange={e => setRecipientsRaw(e.target.value)}
                placeholder="ac-member1@example.com, ac-member2@example.com"
                rows={2}
                className="w-full bg-secondary border border-border rounded-sm p-2.5 text-sm text-foreground resize-y focus:outline-none focus:border-purple-500"
                data-testid="ac-recipients-input" />
            </div>

            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Send from (connected Microsoft account)</p>
              {connections.length === 0 ? (
                <p className="text-xs text-amber-400">No Microsoft accounts connected — connect one under Settings first.</p>
              ) : (
                <Select value={senderId} onValueChange={setSenderId}>
                  <SelectTrigger className="bg-secondary border-border rounded-sm h-9 text-sm" data-testid="ac-sender-select">
                    <SelectValue placeholder="Select sender account" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border rounded-sm">
                    {connections.map(c => (
                      <SelectItem key={c.user_id} value={c.user_id} className="text-xs">
                        {c.name} ({c.account}){c.mail_ready ? '' : ' — no mail permission'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {sender && !sender.mail_ready && (
                <div className="flex items-start gap-2 mt-2 bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-amber-300">
                    This account hasn't granted email permission yet — reconnect Microsoft under Settings → Microsoft To Do.
                  </p>
                </div>
              )}
            </div>

            {(lastSentAt || lastError) && (
              <div className="text-[11px] text-muted-foreground border-t border-border pt-2" data-testid="ac-last-status">
                {lastSentAt && <p>Last sent: {new Date(lastSentAt).toLocaleString('de-DE')}</p>}
                {lastError && <p className="text-red-400">Last error: {lastError}</p>}
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
              <Button onClick={handleSendNow} disabled={sending || saving} variant="outline"
                data-testid="ac-send-now-btn"
                className="border-purple-600/30 text-purple-400 hover:bg-purple-600/10 rounded-sm text-xs h-8 px-3">
                {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                Send now
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => onOpenChange(false)}
                  className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} data-testid="ac-settings-save-btn"
                  className="bg-purple-600 hover:bg-purple-500 text-white rounded-sm text-xs h-8 px-4">
                  {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
